import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../components/SafeIcon';
import { supabase } from '@/integrations/supabase/client';
import * as FiIcons from 'react-icons/fi';
import snapChartLogo from '@/assets/snap-chart-logo.png';
import ProfileModal from '../components/ProfileModal';
import SecurityModal from '../components/SecurityModal';
import TrialBanner from '../components/TrialBanner';
import KYCVerificationModal from '../components/kyc/KYCVerificationModal';
import OnboardingFlow from '../components/onboarding/OnboardingFlow';
import VerdictShareCard from '../components/VerdictShareCard';
import { calculateTDIScore } from '@/services/disciplineScoreService';

const {
  FiUser, FiSettings, FiTrendingUp, FiDollarSign, FiActivity, FiShield,
  FiBarChart2, FiPieChart, FiTarget, FiClock, FiCheck, FiX, FiEye,
  FiLock, FiKey, FiUserCheck, FiUsers, FiBook, FiMonitor, FiGlobe,
  FiDatabase, FiWifi, FiZap, FiChevronRight, FiCpu, FiLayers, FiFolder, FiShare2
} = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const { profile, usage, isAdmin, loading } = useSubscription();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [capitalPreserved, setCapitalPreserved] = useState(null); // { totalUsd, totalR, decisions }
  const [viralStats, setViralStats] = useState(null); // { shares, clicks, signups, top_channel }
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  // Real dashboard metrics (streak + recent verdicts) — see get_my_dashboard_metrics RPC
  const [dashMetrics, setDashMetrics] = useState({ streak_days: null, recent_verdicts: null, total_entries: null });
  // REAL TDI score — same source as the Behavior & Discipline modal (calculate_tdi_score_v2)
  // Shape: { tdi_score, tier (DEVELOPING|FAIR|GOOD|EXCELLENT), trend, trend_change, decisions_counted }
  const [tdiData, setTdiData] = useState(null);
  // Verdict picked for the 1080×1080 share-card modal
  const [shareVerdict, setShareVerdict] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  // showAdminTools removed — the state was never toggled, so the panel
  // below never opened. The single "User Management" link it exposed is
  // already reachable via the top-nav ADMIN button + the /admin route.
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [hasDataRoomAccess, setHasDataRoomAccess] = useState(false);
  const [isCTOManager, setIsCTOManager] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Check if user has access to any data rooms
  useEffect(() => {
    const checkDataRoomAccess = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.rpc('get_user_data_rooms');
        console.log('📂 Data room check:', data, error);
        if (!error && data?.success && data?.data_rooms?.length > 0) {
          setHasDataRoomAccess(true);
        }
      } catch (err) {
        console.error('Error checking data room access:', err);
      }
    };
    checkDataRoomAccess();
  }, [user]);

  // Fetch the total Capital Preserved by AI for this user
  // (sum of capital_preserved_usd across COMPLETED counterfactual records).
  // Populated by the Vercel cron robot api/snapshot-counterfactuals; on cold
  // start the total may be 0 until the first cron run.
  useEffect(() => {
    const fetchCapitalPreserved = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('counterfactual_tracking')
          .select('capital_preserved_usd, capital_preserved_r')
          .eq('user_id', user.id)
          .eq('tracking_status', 'COMPLETED');
        if (error) { console.error('[dashboard] capital_preserved fetch:', error.message); return; }
        // Marketing rule: the "Capital Saved" card shows ONLY the WINS
        // (decisions where the AI was right to say NO-TRADE -> capital_preserved_usd > 0).
        // Net would include misses (the AI said WAIT and the trade would have won),
        // which can turn the headline number NEGATIVE -> kills conversion.
        // Per-trade card (CounterfactualProof) still shows the honest +/- per decision.
        const wins = (data || []).filter(r => Number(r.capital_preserved_usd) > 0);
        const decisions = wins.length;
        const totalUsd = wins.reduce((s, r) => s + Number(r.capital_preserved_usd), 0);
        const totalR = wins.reduce((s, r) => s + Number(r.capital_preserved_r), 0);
        setCapitalPreserved({ totalUsd, totalR, decisions });
      } catch (err) {
        console.error('[dashboard] capital_preserved error:', err);
      }
    };
    fetchCapitalPreserved();
  }, [user]);

  // Fetch the user's viral stats (shares / clicks / signups) for the
  // "Your Viral Impact" card. Lazy: the RPC also lazy-generates the
  // share_code via ensure_share_code on first share-button open.
  useEffect(() => {
    const fetchViralStats = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.rpc('get_my_viral_stats');
        if (error) { console.error('[dashboard] viral_stats fetch:', error.message); return; }
        if (data) setViralStats(data);
      } catch (err) {
        console.error('[dashboard] viral_stats error:', err);
      }
    };
    fetchViralStats();
  }, [user]);

  // Lazy-generate (or fetch) the user's share_code for the "Copy your link"
  // button below the viral card.
  const [myShareCode, setMyShareCode] = useState(null);
  useEffect(() => {
    if (!user) return;
    supabase.rpc('ensure_share_code').then(({ data, error }) => {
      if (!error && typeof data === 'string') setMyShareCode(data);
    });
  }, [user]);

  // Real dashboard metrics — streak + recent verdicts (drives the v3 block)
  useEffect(() => {
    if (!user) return;
    // Pass the browser's tz offset (negated) so the streak is counted in the
    // USER'S local calendar, not UTC. Fixes streak resets for users west of
    // UTC (EST/PST) who analyse in the evening.
    supabase.rpc('get_my_dashboard_metrics', {
      p_tz_offset_minutes: -new Date().getTimezoneOffset(),
    }).then(({ data, error }) => {
      if (error) { console.warn('[dashboard] dashboard_metrics fetch:', error.message); return; }
      if (data && typeof data === 'object') setDashMetrics(data);
    });
  }, [user]);

  // REAL TDI fetch — same RPC as the Behavior & Discipline modal so the
  // dashboard number always matches what the user sees in the audit view.
  useEffect(() => {
    if (!user) return;
    calculateTDIScore(30).then((result) => {
      if (result?.success && result?.tdi_score !== null) setTdiData(result);
    }).catch((err) => console.warn('[dashboard] tdi fetch:', err?.message));
  }, [user]);

  const copyMyShareLink = () => {
    const link = `https://snap-chart.ai/?ref=${myShareCode || 'share'}`;
    try {
      navigator.clipboard.writeText(link);
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2000);
    } catch (err) { console.error('Copy failed:', err); }
  };

  // Check if user is a CTO manager
  useEffect(() => {
    const checkCTOAccess = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.rpc('is_cto_manager');
        if (!error && data === true) {
          setIsCTOManager(true);
        }
      } catch (err) {
        console.error('Error checking CTO access:', err);
      }
    };
    checkCTOAccess();
  }, [user]);

  // Fetch user dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;

      try {
        console.log('📊 Fetching dashboard stats...');
        
        // Use get_journal_stats which exists
        const { data: journalStats, error: journalError } = await supabase
          .rpc('get_journal_stats', { p_user_id: user.id });

        if (!journalError && journalStats) {
          console.log('✅ Journal stats fetched:', journalStats);
          
          // Calculate win rate based on completed trades (WIN + LOSS), not all trades
          const completedTrades = (journalStats.wins || 0) + (journalStats.losses || 0);
          const winRate = completedTrades > 0 
            ? Math.round((journalStats.wins / completedTrades) * 100) 
            : 0;
          
          setStats({
            total_analyses: journalStats.total_analyses || 0,
            // Use executed_trades (WIN/LOSS/BREAKEVEN) not trade_decisions (all TRADE entries)
            total_trades: journalStats.executed_trades || 0,
            win_rate: winRate,
            total_pnl: journalStats.total_r_multiple || 0,
          });
        } else {
          console.log('⚠️ Journal stats failed, using usage count:', journalError?.message);
          setStats({
            total_analyses: usage?.count || 0,
            total_trades: 0,
            win_rate: 0,
            total_pnl: 0,
          });
        }
      } catch (error) {
        console.error('❌ Error fetching stats:', error);
        setStats({
          total_analyses: usage?.count || 0,
          total_trades: 0,
          win_rate: 0,
          total_pnl: 0,
        });
      }
    };

    fetchStats();
  }, [user, usage]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'New passwords do not match' });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordStatus({ type: 'error', message: 'Password must be at least 6 characters' });
      return;
    }

    setPasswordLoading(true);
    setPasswordStatus(null);

    try {
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.currentPassword
      });

      if (verifyError) {
        setPasswordStatus({ type: 'error', message: 'Current password is incorrect' });
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      });

      if (updateError) {
        setPasswordStatus({ type: 'error', message: updateError.message });
      } else {
        setPasswordStatus({ type: 'success', message: 'Password updated successfully!' });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordStatus(null);
        }, 2000);
      }
    } catch (error) {
      setPasswordStatus({ type: 'error', message: 'An unexpected error occurred' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleStartTrading = () => navigate('/trading');
  const handleViewCharts = () => navigate('/trading');
  const handleSettings = () => setShowPasswordModal(true);
  const handleHistory = () => navigate('/trading');

  const handleProfileUpdate = () => {
    // Refresh profile data after update
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // If loading is done but no profile yet, try to refresh or show temporary state
  if (!profile && user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!profile && !user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiX} className="text-red-500 text-4xl mb-4 mx-auto" />
          <p className="text-white text-lg">Please log in to access the dashboard</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Brand-new users: show the 3-step onboarding before the dashboard.
  if (profile?.first_login && user) {
    return <OnboardingFlow userId={user.id} onComplete={() => window.location.reload()} />;
  }

  const getSubscriptionBadge = () => {
    if (isAdmin) return { text: 'Admin Access', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30', textColor: 'text-red-400' };
    
    const planId = profile?.subscription_plan_id;
    const billingType = profile?.billing_type;
    const isInTrial = profile?.is_in_trial;
    
    // Check billing type for lifetime
    const lifetimeSuffix = billingType === 'lifetime' ? ' (Lifetime)' : '';
    const trialSuffix = isInTrial ? ' (Trial)' : '';
    
    switch (planId) {
      case 'enterprise':
        return { text: `Enterprise${lifetimeSuffix}`, bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30', textColor: 'text-amber-400' };
      case 'institutional_ai':
        return { text: `Institutional AI${lifetimeSuffix}${trialSuffix}`, bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30', textColor: 'text-emerald-400' };
      case 'pro_trader':
        return { text: `Pro Trader${lifetimeSuffix}${trialSuffix}`, bgColor: 'bg-violet-500/10', borderColor: 'border-violet-500/30', textColor: 'text-violet-400' };
      case 'starter':
        return { text: `Starter${lifetimeSuffix}`, bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30', textColor: 'text-blue-400' };
      default:
        // If in trial without plan, show trial status
        if (isInTrial) {
          return { text: 'Trial (Institutional AI)', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30', textColor: 'text-purple-400' };
        }
        return { text: 'Free Plan', bgColor: 'bg-gray-500/10', borderColor: 'border-gray-500/30', textColor: 'text-gray-400' };
    }
  };

  const badge = getSubscriptionBadge();

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Subtle gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/20 via-gray-950 to-cyan-950/20 pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Trial Banner */}
        <TrialBanner />
        
        {/* ═══════════ HEADER — v3 2026: identity + big primary CTA + settings chips ═══════════ */}
        <motion.header
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          {/* Identity row */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-shrink-0">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden flex items-center justify-center">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <SafeIcon icon={FiUser} className="text-xl text-cyan-400" />
                )}
              </div>
              {isAdmin && (
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center ring-2 ring-gray-950">
                  <SafeIcon icon={FiShield} className="text-white text-[10px]" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              {/* Time-based greeting — chooses one of good morning / afternoon
                  / evening / late-night based on the user's own timezone (falls
                  back to the browser TZ). Warmer than a static "welcome back"
                  and matches what Linear / Notion / Framer surface. */}
              {(() => {
                const tz = profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                const hour = Number(new Intl.DateTimeFormat('en-US', {
                  timeZone: tz, hour: 'numeric', hour12: false,
                }).format(new Date()));
                const greeting =
                  hour < 5 ? 'late night' :
                  hour < 12 ? 'good morning' :
                  hour < 18 ? 'good afternoon' :
                  hour < 22 ? 'good evening' : 'late night';

                // Prefer explicit display name; fall back through first_name,
                // full name field, then a Title-Cased email prefix. Never show
                // raw handles like 'trendykingsuccess' if we have something better.
                const raw =
                  profile?.first_name?.trim() ||
                  profile?.name?.trim().split(' ')[0] ||
                  user?.email?.split('@')[0] ||
                  'Trader';
                const displayName = /^[a-z0-9_.-]+$/i.test(raw) && !/\s/.test(raw) && raw.length > 4
                  ? raw.split(/[._-]+/).map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ')
                  : raw;

                const longDate = new Date().toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric',
                });

                return (
                  <>
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-0.5">
                      ▸ {greeting}
                    </div>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white truncate leading-tight">
                      {displayName}.
                    </h1>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className={`text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded ${badge.bgColor} border ${badge.borderColor} ${badge.textColor}`}>
                        {badge.text}
                      </span>
                      <span className="text-gray-500 text-[11px] font-mono tracking-wider hidden sm:inline">
                        {longDate}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* PRIMARY CTA — the ONE thing the user came here to do */}
          <button
            onClick={() => navigate('/trading')}
            className="group relative w-full overflow-hidden rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-gray-900/40 to-purple-500/10 p-5 sm:p-6 text-left hover:border-cyan-400/60 transition-all hover:scale-[1.005] shadow-2xl shadow-cyan-500/10 mb-6">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-400 flex items-center justify-center flex-shrink-0 shadow-xl shadow-cyan-500/30">
                  <SafeIcon icon={FiCpu} className="text-2xl sm:text-3xl text-gray-900" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1">▸ start here</div>
                  <div className="text-xl sm:text-2xl font-black text-white leading-tight mb-1">
                    Capture a chart, get a verdict
                  </div>
                  <div className="text-sm text-gray-400">
                    Open the built-in chart, press <span className="font-mono text-cyan-300">📸 Capture</span>, or drag any screenshot.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-gray-900 font-bold text-sm shadow-xl shadow-cyan-500/30 group-hover:scale-105 transition flex-shrink-0 whitespace-nowrap">
                Open trading <SafeIcon icon={FiChevronRight} className="group-hover:translate-x-0.5 transition" />
              </div>
            </div>
          </button>

          {/* Settings chips — unified mono uppercase v3 style, scrollable on mobile */}
          <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto scrollbar-thin">
            <div className="flex items-center gap-2 sm:flex-wrap">
              {[
                { label: 'Profile',      icon: FiUser,      onClick: () => setShowProfileModal(true),     show: true,           tone: 'gray' },
                // Verification / KYC hidden for everyone (admin included) —
                // Snap Chart AI does not handle custody of funds so KYC is
                // not legally required, and it added friction without value.
                // To re-enable the day a regulated feature ships: flip
                // `show: false` → `show: true` (or `isAdmin`).
                { label: 'Verification', icon: FiUserCheck, onClick: () => setShowKYCModal(true),         show: false,          tone: 'gray' },
                { label: 'Security',     icon: FiShield,    onClick: () => setShowSecurityModal(true),    show: true,           tone: 'gray' },
                { label: 'Billing',      icon: FiDollarSign,onClick: () => navigate('/account/billing'),  show: true,           tone: 'gray' },
                // Data Room removed from user-facing navbar — accessible via
                // the Admin panel only (Admin → Data Rooms). Reversible by
                // flipping show: false → isAdmin.
                { label: 'Data Room',    icon: FiFolder,    onClick: () => navigate('/data-rooms'),       show: false,          tone: 'gray' },
                // CTO Tasks moved to Admin panel only. Reversible by flipping
                // show: false → (isCTOManager || isAdmin).
                { label: 'CTO Tasks',    icon: FiLayers,    onClick: () => navigate('/cto-tasks'),        show: false,          tone: 'gray' },
                { label: 'Admin',        icon: FiShield,    onClick: () => navigate('/admin'),            show: isAdmin,        tone: 'red' },
              ].filter(c => c.show).map((c) => (
                <button key={c.label} onClick={c.onClick}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-mono uppercase tracking-[0.15em] transition flex-shrink-0 ${
                    c.tone === 'red'
                      ? 'border-red-500/30 bg-red-500/5 text-red-300 hover:bg-red-500/10 hover:border-red-500/50'
                      : 'border-white/10 bg-white/[0.02] text-gray-400 hover:text-cyan-300 hover:border-cyan-400/30 hover:bg-cyan-500/5'
                  }`}>
                  <SafeIcon icon={c.icon} className="text-xs" /> {c.label}
                </button>
              ))}
            </div>
          </div>
        </motion.header>

        {/* Admin Tools Panel removed — was dead code (state never toggled).
            Admins reach /admin via the ADMIN top-nav button. */}

        {/* ═══════════ TDI + STREAK + TODAY'S VERDICT — $1B viral v3 block ═══════════ */}
        <motion.section
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="mb-6"
        >
          {/* Row 1: TDI Score + Streak side-by-side */}
          <div className="grid grid-cols-12 gap-3 mb-3">
            {/* TDI SCORE — REAL data from calculate_tdi_score_v2 RPC.
                FICO scale 300-850. Tiers: DEVELOPING / FAIR / GOOD / EXCELLENT.
                Same source as the Behavior & Discipline modal so numbers always match. */}
            {(() => {
              const score = tdiData?.tdi_score ?? null;
              const tier = (tdiData?.tier || (
                score === null ? 'PENDING' :
                score >= 740 ? 'EXCELLENT' :
                score >= 670 ? 'GOOD' :
                score >= 580 ? 'FAIR' : 'DEVELOPING'
              )).toUpperCase();
              const tierMap = {
                EXCELLENT:  { hex: '#10b981', chip: 'bg-emerald-500/20 text-emerald-300', border: 'border-emerald-400/30' },
                GOOD:       { hex: '#22d3ee', chip: 'bg-cyan-500/20 text-cyan-300',       border: 'border-cyan-400/30' },
                FAIR:       { hex: '#a855f7', chip: 'bg-purple-500/20 text-purple-300',   border: 'border-purple-400/30' },
                DEVELOPING: { hex: '#f59e0b', chip: 'bg-amber-500/20 text-amber-300',     border: 'border-amber-400/30' },
                PENDING:    { hex: '#9ca3af', chip: 'bg-gray-500/20 text-gray-300',       border: 'border-white/10' },
              }[tier] || { hex: '#9ca3af', chip: 'bg-gray-500/20 text-gray-300', border: 'border-white/10' };
              const decisions = tdiData?.decisions_counted ?? 0;
              const trendChange = tdiData?.trend_change ?? 0;
              const trendDir = tdiData?.trend; // 'UP' | 'DOWN' | 'STABLE' | 'NEW'
              // Position on FICO scale 300-850 (range 550)
              const pos = score !== null ? Math.max(0, Math.min(1, (score - 300) / 550)) : 0;
              return (
                <div className={`col-span-12 lg:col-span-7 relative overflow-hidden rounded-2xl border ${tierMap.border} bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/40 p-5 lg:p-6`}>
                  <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: `${tierMap.hex}26` }} />
                  <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">tdi score · discipline index</span>
                        <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${tierMap.chip}`}>
                          {tier}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                        <span className="text-5xl sm:text-6xl font-black font-mono tabular-nums leading-none" style={{ color: tierMap.hex }}>
                          {score !== null ? score : '—'}
                        </span>
                        <span className="text-xl font-mono text-gray-500">/850</span>
                        {trendDir && trendDir !== 'NEW' && trendChange !== 0 && (
                          <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${trendDir === 'UP' ? 'bg-emerald-500/15 text-emerald-300' : trendDir === 'DOWN' ? 'bg-red-500/15 text-red-300' : 'bg-gray-500/15 text-gray-400'}`}>
                            {trendDir === 'UP' ? '↑' : trendDir === 'DOWN' ? '↓' : '→'} {Math.abs(trendChange)}
                          </span>
                        )}
                      </div>
                      {/* FICO-style bar */}
                      <div className="relative w-full h-2 rounded-full bg-white/[0.05] overflow-hidden mb-2">
                        <div className="absolute inset-y-0 left-0 rounded-full transition-all"
                          style={{ width: `${pos * 100}%`, background: `linear-gradient(90deg, #f59e0b, #a855f7, #22d3ee, #10b981)` }} />
                      </div>
                      <div className="flex justify-between text-[9px] font-mono uppercase tracking-wider text-gray-600 mb-3">
                        <span>300</span><span>developing</span><span>fair</span><span>good</span><span>excellent</span><span>850</span>
                      </div>
                      <p className="text-xs text-gray-500 font-mono">
                        {decisions > 0 ? `based on ${decisions} decision${decisions > 1 ? 's' : ''} · last 30 days` : 'need 5+ decisions in 30 days to score'}
                      </p>
                    </div>
                    {/* Radial gauge — position on FICO scale */}
                    <div className="hidden sm:flex flex-col items-center flex-shrink-0">
                      <div className="relative w-28 h-28">
                        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                          <circle cx="50" cy="50" r="44" fill="none" stroke={tierMap.hex}
                            strokeWidth="8" strokeLinecap="round"
                            strokeDasharray={`${pos * 276.46} 276.46`} />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-[9px] font-mono uppercase tracking-wider text-gray-500">score</span>
                          <span className="text-xl font-black tabular-nums" style={{ color: tierMap.hex }}>
                            {score !== null ? score : '—'}
                          </span>
                          <span className="text-[9px] font-mono uppercase tracking-wider text-gray-600">{tier}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* STREAK — Duolingo-style retention (REAL consecutive-days from DB) */}
            {(() => {
              const streak = dashMetrics?.streak_days ?? 0;
              const isActive = streak > 0;
              return (
                <div className="col-span-12 lg:col-span-5 relative overflow-hidden rounded-2xl border border-orange-400/30 bg-gradient-to-br from-orange-500/10 via-gray-900/60 to-red-500/5 p-5 lg:p-6">
                  <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-500/15 rounded-full blur-3xl pointer-events-none" />
                  <div className="relative">
                    <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-orange-300">discipline streak</span>
                    <div className="flex items-baseline gap-3 mt-2 mb-2">
                      <span className="text-5xl sm:text-6xl">🔥</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-5xl sm:text-6xl font-black font-mono tabular-nums text-orange-200 leading-none">{streak}</span>
                        <span className="text-lg font-mono text-orange-400/60">{streak === 1 ? 'day' : 'days'}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-300 leading-snug">
                      {isActive
                        ? <>Used AI {streak === 1 ? 'today' : `for ${streak} consecutive days`}. <span className="text-orange-200 font-semibold">Don't break it.</span></>
                        : <>Capture a chart today to start your streak. <span className="text-orange-200 font-semibold">Day 1 begins now.</span></>}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Row 2: YOUR LATEST VERDICTS — real journal data + share-built-in */}
          {(() => {
            const raw = Array.isArray(dashMetrics?.recent_verdicts) ? dashMetrics.recent_verdicts : [];
            const verdictTone = (v) => {
              const k = (v || '').toUpperCase().replace('_', '-');
              if (k === 'NO-TRADE' || k === 'NO_TRADE') return 'purple';
              if (k === 'WAIT' || k === 'CONDITIONAL') return 'amber';
              if (k === 'TRADE') return 'emerald';
              return 'gray';
            };
            const fmtPrice = (p) => {
              if (p === null || p === undefined) return '—';
              const n = Number(p); if (!Number.isFinite(n)) return '—';
              return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
            };
            const fmtVerdict = (v) => (v || '').replace('_', '-').toUpperCase();
            const fmtAge = (iso) => {
              if (!iso) return '';
              const d = new Date(iso); const diff = Date.now() - d.getTime();
              const mins = Math.floor(diff / 60000);
              if (mins < 1) return 'just now';
              if (mins < 60) return `${mins}m ago`;
              const hrs = Math.floor(mins / 60);
              if (hrs < 24) return `${hrs}h ago`;
              const days = Math.floor(hrs / 24);
              return `${days}d ago`;
            };
            const hasData = raw.length > 0;
            const shareText = hasData
              ? `My latest SnapChart AI verdicts:\n\n${raw.slice(0, 3).map(v => `$${v.asset} ${fmtPrice(v.price)} → ${fmtVerdict(v.verdict)} (${v.conf || '—'}% conf)`).join('\n')}\n\nThe AI that tells you when NOT to trade.\nsnap-chart.ai`
              : '';

            return (
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-gray-900/80 to-gray-900/40 p-5 lg:p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">your latest ai verdicts</span>
                    <h3 className="text-lg sm:text-xl font-bold text-white mt-1">
                      Last {Math.min(3, raw.length || 0)} analyses · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </h3>
                  </div>
                  {hasData && (
                    <button
                      onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank')}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-500/5 text-xs font-mono uppercase tracking-[0.15em] text-gray-300 hover:text-cyan-300 transition">
                      <span className="font-black text-base leading-none">𝕏</span> share these
                    </button>
                  )}
                </div>
                {hasData ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {raw.slice(0, 3).map((v, i) => {
                      const tone = verdictTone(v.verdict);
                      return (
                        <div key={i} className={`group relative p-4 rounded-xl border transition ${
                          tone === 'purple' ? 'border-purple-500/30 bg-purple-500/5 hover:border-purple-400/60' :
                          tone === 'amber'  ? 'border-amber-500/30 bg-amber-500/5 hover:border-amber-400/60'  :
                          tone === 'emerald'? 'border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-400/60' :
                                              'border-white/10 bg-white/[0.02] hover:border-white/20'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono text-xs uppercase tracking-wider text-gray-400">{v.asset || '—'}</span>
                            {/* Show price only when we actually captured one — legacy entries
                                where the feed was down store input_price=NULL. */}
                            {v.price != null && (
                              <span className="text-xs font-mono text-white tabular-nums">{fmtPrice(v.price)}</span>
                            )}
                          </div>
                          <div className={`text-2xl font-black font-mono mb-1 ${
                            tone === 'purple' ? 'text-purple-300' :
                            tone === 'amber'  ? 'text-amber-300'  :
                            tone === 'emerald'? 'text-emerald-300':
                                                'text-gray-300'
                          }`}>{fmtVerdict(v.verdict)}</div>
                          <div className="flex items-center justify-between text-[11px]">
                            {/* Confidence label only rendered when the AI returned a number.
                                Empty slot on the left keeps the age flush-right, no '—% conf'. */}
                            <span className="text-gray-500 font-mono">
                              {v.conf != null ? `${v.conf}% conf` : ''}
                            </span>
                            <span className="text-gray-400">{fmtAge(v.created_at)}</span>
                          </div>
                          {/* Per-verdict share — opens the 1080×1080 modal with
                              X / IG / TikTok / WhatsApp / Copy channels (same
                              pattern as the full ShareAnalysisCard on /trading). */}
                          <button
                            onClick={() => setShareVerdict({ ...v, _tone: tone })}
                            className="absolute top-2 right-2 w-8 h-8 rounded-lg border border-white/10 bg-gray-900/70 backdrop-blur-sm text-gray-300 hover:text-cyan-300 hover:border-cyan-400/50 hover:bg-cyan-500/10 opacity-0 group-hover:opacity-100 transition flex items-center justify-center shadow-lg"
                            title="Share as 1080×1080 image (X / IG / TikTok / WhatsApp)">
                            <SafeIcon icon={FiShare2} className="text-sm" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 border border-dashed border-white/10 rounded-xl">
                    <p className="text-sm text-gray-400 mb-3">No analyses yet — your latest 3 verdicts will appear here.</p>
                    <button onClick={() => navigate('/trading')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold text-sm hover:scale-105 transition">
                      Run my first analysis <SafeIcon icon={FiChevronRight} />
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </motion.section>

        {/* Stats Grid - Journal Style */}
        {stats && stats.total_analyses === 0 ? (
          // Empty state — big CTA instead of zero-value stats cards
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <div className="relative overflow-hidden rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 via-gray-900/80 to-cyan-900/30 p-8 sm:p-12 text-center">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(168,85,247,0.18),transparent_60%)] pointer-events-none" />
              <div className="relative">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-600/30 to-cyan-600/30 border border-purple-500/40 flex items-center justify-center">
                  <SafeIcon icon={FiZap} className="text-3xl text-cyan-300" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Run your first analysis in 30 seconds</h2>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Snap a chart screenshot — get an institutional-grade verdict (TRADE / WAIT / NO-TRADE) with discipline built-in.
                </p>
                <button
                  onClick={() => navigate('/trading')}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-semibold text-base shadow-lg shadow-purple-600/30 transition-all hover:scale-105"
                >
                  <SafeIcon icon={FiZap} />
                  Run my first analysis
                  <SafeIcon icon={FiChevronRight} />
                </button>
                <div className="text-xs text-gray-500 mt-4">Your free credits are ready — no setup needed.</div>
              </div>
            </div>
          </motion.section>
        ) : (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-5 rounded-2xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 transition-all backdrop-blur-sm"
          >
            <div className="text-2xl lg:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{stats?.total_analyses || 0}</span>
            </div>
            <div className="text-xs mt-1 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Completed Analyses</span>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.23 }}
            className="p-5 rounded-2xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 transition-all backdrop-blur-sm"
          >
            <div className="text-2xl lg:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">{stats?.total_trades || 0}</span>
            </div>
            <div className="text-xs mt-1 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Executed Trades</span>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.26 }}
            className="p-5 rounded-2xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 transition-all backdrop-blur-sm"
          >
            <div className="text-2xl lg:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{stats?.win_rate || 0}%</span>
            </div>
            <div className="text-xs mt-1 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Trade Win Rate</span>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.29 }}
            className="p-5 rounded-2xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 transition-all backdrop-blur-sm"
          >
            <div className="text-2xl lg:text-3xl font-bold tracking-tight">
              <span className={`${(stats?.total_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(stats?.total_pnl || 0) >= 0 ? '+' : '-'}${Math.abs(stats?.total_pnl || 0).toLocaleString()}
              </span>
            </div>
            <div className="text-xs mt-1 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Net P&L</span>
            </div>
          </motion.div>
        </motion.section>
        )}

        {/* 💰 Capital Saved by AI — the counterfactual dollar hook.
            Renders for any signed-in user (even at $0) so brand-new users
            see the target metric that motivates them to come back. Growth
            plan §1.2: "P4 — TDI as centerpiece + dollar counterfactual …
            People return to watch their own number." We were previously
            hiding at $0, which killed that loop for new signups. */}
        {(() => {
          const usd = capitalPreserved?.totalUsd ?? 0;
          const decisions = capitalPreserved?.decisions ?? 0;
          const hasWins = usd > 0 && decisions > 0;

          return (
            <motion.section
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-6"
            >
              <div className={`relative overflow-hidden rounded-2xl border p-6 sm:p-8 ${
                hasWins
                  ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-900/30 via-gray-900/70 to-cyan-900/20'
                  : 'border-white/10 bg-gradient-to-br from-gray-900/70 via-gray-900/70 to-cyan-900/10'
              }`}>
                <div className={`absolute inset-0 pointer-events-none ${
                  hasWins
                    ? 'bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.18),transparent_60%)]'
                    : 'bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.10),transparent_60%)]'
                }`} />
                <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <div>
                    <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-2 ${
                      hasWins ? 'text-emerald-400' : 'text-cyan-400'
                    }`}>
                      <SafeIcon icon={FiShield} className="w-4 h-4" />
                      Capital Saved by AI
                    </div>
                    {hasWins ? (
                      <>
                        <div className="flex items-baseline gap-3">
                          <span className="text-4xl sm:text-5xl font-bold text-emerald-400">
                            +${usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                          <span className="text-lg font-semibold text-emerald-400/80">
                            (+{capitalPreserved.totalR.toFixed(1)}R)
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mt-2 max-w-md">
                          Losses avoided across <span className="text-white font-semibold">{decisions}</span> disciplined NO-TRADE / WAIT decisions where the AI&apos;s call was right.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-3">
                          <span className="text-4xl sm:text-5xl font-bold text-white/70">
                            $0
                          </span>
                          <span className="text-lg font-mono text-gray-500 uppercase tracking-widest">
                            waiting for your first win
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mt-2 max-w-md">
                          Every time the AI says <span className="text-cyan-400 font-semibold">NO-TRADE</span> and price moves against your setup, the dollars you would&apos;ve lost show up here.
                          <br />
                          <button
                            onClick={() => navigate('/trading')}
                            className="mt-2 inline-block text-cyan-300 hover:text-cyan-200 underline underline-offset-4 text-xs font-medium"
                          >
                            Capture your next chart →
                          </button>
                        </p>
                      </>
                    )}
                  </div>
                  <div className="text-right text-xs text-gray-500 italic max-w-[14rem]">
                    &quot;Anyone can take a trade. Professionals know when NOT to.&quot;
                  </div>
                </div>
              </div>
            </motion.section>
          );
        })()}

        {/* 🌐 Your Viral Impact — only renders if the user has at least one
            share OR one inbound click attributed to them. Avoids showing an
            empty zero-card to fresh users. */}
        {stats && stats.total_analyses > 0 && viralStats && (viralStats.shares > 0 || viralStats.clicks > 0) && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-900/20 via-gray-900/70 to-purple-900/20 p-6 sm:p-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.15),transparent_60%)] pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-cyan-400 mb-4">
                  <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  Your Viral Impact
                </div>

                <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-5">
                  <div>
                    <div className="text-3xl sm:text-4xl font-bold text-white">{viralStats.shares}</div>
                    <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Shares</div>
                  </div>
                  <div className="border-l border-white/10 pl-4 sm:pl-6">
                    <div className="text-3xl sm:text-4xl font-bold text-cyan-400">{viralStats.clicks}</div>
                    <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Visits</div>
                  </div>
                  <div className="border-l border-white/10 pl-4 sm:pl-6">
                    <div className={`text-3xl sm:text-4xl font-bold ${viralStats.signups > 0 ? 'text-emerald-400' : 'text-gray-500'}`}>{viralStats.signups}</div>
                    <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Signups</div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-white/5">
                  <div className="flex-grow text-xs text-gray-400">
                    Your share link:&nbsp;
                    <code className="text-cyan-400 font-mono">snap-chart.ai/?ref={myShareCode || '...'}</code>
                  </div>
                  <button
                    onClick={copyMyShareLink}
                    disabled={!myShareCode}
                    className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 text-sm font-medium hover:bg-cyan-500/20 transition disabled:opacity-50 whitespace-nowrap"
                  >
                    {shareLinkCopied ? '✓ Copied' : 'Copy link'}
                  </button>
                </div>
              </div>
            </div>
          </motion.section>
        )}

        {/* Affiliate Program CTA removed from the dashboard.
            Simplification pass: the goal here is to keep users focused on
            "analyze my next chart", not to upsell every visit. The affiliate
            program stays reachable via the top-nav (AFFILIATE link) + from
            the /affiliate route + from footer, which is enough surface for
            the ~2% of users who actually want to share. */}

        {/* CTA - Full Width Premium */}
        {/* Bottom CTA removed — redundant with primary CTA at the top of the header */}

      </div>

      {/* Password Change Modal - 2026 Style */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 p-6 rounded-2xl border border-white/10 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <SafeIcon icon={FiKey} className="w-5 h-5 text-purple-400" />
                  Change Password
                </h3>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {passwordStatus && (
                <div className={`mb-4 p-4 rounded-xl ${
                  passwordStatus.type === 'success' 
                    ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`}>
                  {passwordStatus.message}
                </div>
              )}

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    required
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    required
                    minLength="6"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    required
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 text-gray-300 hover:from-purple-600/20 hover:to-cyan-600/20 hover:text-cyan-400 transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 font-medium hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  >
                    {passwordLoading ? (
                      <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Verdict share-card modal — 1080×1080 + multi-channel (matches ShareAnalysisCard) */}
      {shareVerdict && (
        <VerdictShareCard
          verdict={shareVerdict}
          onClose={() => setShareVerdict(null)}
          userHandle={profile?.first_name?.toLowerCase().replace(/\s+/g, '') || user?.email?.split('@')[0] || 'snapchartai'}
        />
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        userProfile={profile}
        onUpdate={handleProfileUpdate}
      />

      {/* Security Modal */}
      <SecurityModal
        isOpen={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        userEmail={user?.email}
      />

      {/* KYC Verification Modal */}
      <KYCVerificationModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
      />
    </div>
  );
};

export default Dashboard;
